import React, { useCallback, useEffect, useState } from 'react'
import { practiceApi, errorMessage } from './api'
import { LANGUAGE_LABELS, LEVEL_LABELS, formatDuration } from './practiceShared'

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—')
const REFRESH_MS = 30000

// Admin view of the writing-a-program practice: who solved what, on their own or with hints.
export default function StPracticeAdmin() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [personId, setPersonId] = useState(null)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [version, setVersion] = useState(0)

  const load = useCallback(() => {
    setError('')
    return practiceApi.get('/admin/overview')
      .then(({ data }) => { setData(data); setUpdatedAt(new Date()); setVersion(v => v + 1) })
      .catch(err => setError(errorMessage(err)))
  }, [])
  // New attempts show up without reloading the page.
  useEffect(() => {
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) return <div className="std-alert std-alert-bad">{error}</div>
  if (!data) return <div className="std-card">Loading practice progress…</div>

  const { people, problems, totals } = data

  return (
    <>
      <div className="std-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Practice progress — writing a program</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="std-muted std-small">Updates every 30s{updatedAt && ` · last ${updatedAt.toLocaleTimeString()}`}</span>
            <button className="std-btn std-btn-ghost std-btn-sm" onClick={load}>Refresh</button>
          </div>
        </div>
        <p className="std-muted std-small" style={{ marginTop: 0 }}>
          "On their own" means solved without opening any hint. Time counts only while the problem's tab is open, in focus and in use (it pauses after 1 minute without activity).
        </p>
        <div className="std-table-wrap">
          <table className="std-table">
            <thead>
              <tr><th>Person</th><th>Basic</th><th>Tough</th><th>Solved on their own</th><th>Solved with hints</th><th>Started</th><th>Submissions</th><th>Hints opened</th><th>Active time</th><th>Avg time per solve</th><th>Last active</th><th></th></tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.userId}>
                  <td><strong>{p.name}</strong><div className="std-muted std-small">{p.email}</div></td>
                  <td>{p.basicSolved}/{totals.basic}</td>
                  <td>{p.toughSolved}/{totals.tough}</td>
                  <td><span className="std-badge std-badge-ok">{p.solvedOnOwn}</span></td>
                  <td>{p.solvedWithHints > 0 ? <span className="std-badge std-badge-warn">{p.solvedWithHints}</span> : 0}</td>
                  <td>{p.started}</td>
                  <td>{p.submissions}</td>
                  <td>{p.hintsUsed}</td>
                  <td>{formatDuration(p.activeSeconds)}</td>
                  <td>{p.solved ? formatDuration(p.solveSecondsTotal / p.solved) : '—'}</td>
                  <td className="std-small">{fmt(p.lastActiveAt)}</td>
                  <td><button className="std-btn std-btn-ghost std-btn-sm" onClick={() => setPersonId(p.userId)}>View</button></td>
                </tr>
              ))}
              {people.length === 0 && <tr><td colSpan={12} className="std-muted">No one has started practising yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {personId && <PersonDetail key={`${personId}-${version}`} userId={personId} onClose={() => setPersonId(null)} />}

      <div className="std-card">
        <h2>By problem</h2>
        <div className="std-table-wrap">
          <table className="std-table">
            <thead><tr><th>Problem</th><th>Level</th><th>Pattern</th><th>Started</th><th>Solved</th><th>On their own</th><th>Submissions</th><th>Avg time to solve</th></tr></thead>
            <tbody>
              {problems.map(pr => (
                <tr key={pr.id}>
                  <td><strong>{pr.id.toUpperCase()}</strong> {pr.title}</td>
                  <td>{LEVEL_LABELS[pr.level]}</td>
                  <td className="std-small">{pr.pattern}</td>
                  <td>{pr.started}</td>
                  <td>{pr.solved}</td>
                  <td>{pr.solvedOnOwn}</td>
                  <td>{pr.submissions}</td>
                  <td>{formatDuration(pr.avgSolveSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function PersonDetail({ userId, onClose }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    practiceApi.get(`/admin/users/${userId}`).then(({ data }) => setData(data)).catch(err => setError(errorMessage(err)))
  }, [userId])

  if (error) return <div className="std-card"><div className="std-alert std-alert-bad">{error}</div></div>
  if (!data) return <div className="std-card">Loading…</div>

  const touched = data.problems.filter(p => p.progress)

  return (
    <div className="std-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h2 style={{ marginBottom: 2 }}>{data.person?.name} <span className="std-muted std-small">{data.person?.email}</span></h2>
        <button className="std-btn std-btn-ghost std-btn-sm" onClick={onClose}>Close</button>
      </div>
      {touched.length === 0 && <p className="std-muted">No problems started.</p>}
      {touched.map(p => {
        const pr = p.progress
        const status = pr.solved ? (pr.solvedOnOwn ? ['✓ Solved on their own', 'std-badge-ok'] : ['✓ Solved with hints', 'std-badge-ok']) : [pr.submissions ? 'Not solved yet' : pr.plan ? 'Plan only' : 'Opened only', 'std-badge-warn']
        return (
          <details key={p.id} style={{ borderTop: '1px solid var(--std-border)', padding: '10px 0' }}>
            <summary style={{ cursor: 'pointer' }}>
              <strong>{p.id.toUpperCase()} {p.title}</strong> <span className="std-muted std-small">({LEVEL_LABELS[p.level]} · {p.pattern})</span>{' '}
              <span className={`std-badge ${status[1]}`}>{status[0]}</span>{' '}
              <span className="std-muted std-small">{pr.submissions} submission(s){pr.solved && pr.submissionsToSolve ? `, solved on submission ${pr.submissionsToSolve}` : ''} · {pr.hintsUsed}/{p.hints.length} hints opened · {pr.solved ? `solved in ${formatDuration(pr.solveSeconds)} of active time` : `${formatDuration(pr.activeSeconds || 0)} active so far`}</span>
            </summary>
            <div style={{ marginTop: 8 }}>
              <div className="std-small std-muted">Plan ({fmt(pr.planAt)})</div>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 8px' }}>{pr.plan || '—'}</p>
              {pr.hintsUsed > 0 && (
                <div className="std-small" style={{ marginBottom: 8 }}>
                  <span className="std-muted">Hints opened:</span>
                  <ol style={{ margin: '4px 0', paddingLeft: 20 }}>{p.hints.slice(0, pr.hintsUsed).map((h, i) => <li key={i}><strong>{h.title}</strong>{h.code ? ' (includes code)' : `: ${h.text}`}</li>)}</ol>
                </div>
              )}
              {Object.entries(pr.code || {}).map(([lang, code]) => (
                <details key={lang}><summary className="std-small">Latest {LANGUAGE_LABELS[lang]} code</summary><pre className="std-pre">{code}</pre></details>
              ))}
              {p.submissions.length > 0 && (
                <table className="std-table" style={{ marginTop: 8 }}>
                  <thead><tr><th>When</th><th>Language</th><th>Passed</th><th>Failing kinds of input</th><th>Hints open</th></tr></thead>
                  <tbody>
                    {p.submissions.map(s => (
                      <tr key={s._id}>
                        <td className="std-small">{fmt(s.at)}</td>
                        <td>{LANGUAGE_LABELS[s.language]}</td>
                        <td>{s.passed}/{s.total}</td>
                        <td className="std-small">{s.failedLabels.join(', ') || '—'}</td>
                        <td>{s.hintsUsed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </details>
        )
      })}
    </div>
  )
}
